import { Link, useParams } from "react-router-dom";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import FilmCard from "@/components/feature/FilmCard";
import SectionHeading from "@/components/feature/SectionHeading";
import EmptyState from "@/components/base/EmptyState";
import { formatDate, getOtherSpecials, getSpecialById, getSpecialFilms } from "@/lib/catalog";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function SpecialDetailPage() {
  const { id } = useParams();
  const special = getSpecialById(id);

  usePageMeta(
    special ? `${special.title}_日韩伦理影视专题片单_日韩伦理片` : "专题未找到_日韩伦理片",
    special
      ? `${special.title}：${special.subtitle}。${special.intro}`
      : "该专题暂未收录，可返回专题合集浏览其他策划片单。",
  );

  if (!special) {
    return (
      <div className="mx-auto max-w-shell px-4 py-16 md:px-8">
        <Breadcrumbs items={[{ label: "专题", to: "/specials" }, { label: "未找到" }]} />
        <EmptyState
          icon="ri-folder-line"
          title="这个专题不存在"
          description="专题可能已经调整或链接有误。返回专题合集页可以看到当前全部企划。"
        >
          <Link
            to="/specials"
            className="flex cursor-pointer items-center gap-1.5 rounded-md bg-primary-800 px-4 py-2 text-[12px] font-medium text-background-50 transition-colors hover:bg-primary-700"
          >
            返回专题合集
          </Link>
        </EmptyState>
      </div>
    );
  }

  const specialFilms = getSpecialFilms(special);
  const others = getOtherSpecials(special.id, 2);

  return (
    <div>
      <section className="relative w-full overflow-hidden bg-primary-950">
        <img
          src={special.cover}
          alt={`${special.title} 专题主图`}
          className="absolute inset-0 h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950/95 via-primary-950/80 to-primary-950/50" />
        <div className="relative mx-auto max-w-shell px-4 pb-9 pt-6 md:px-8 md:pb-12 md:pt-8">
          <Breadcrumbs items={[{ label: "专题", to: "/specials" }, { label: special.title }]} />
          <div className="max-w-3xl">
            <p className="font-label text-[11px] font-semibold uppercase tracking-[0.24em] text-accent-300">
              {special.subtitle}
            </p>
            <h1 className="mt-2 font-heading text-[32px] font-extrabold leading-tight tracking-tight text-background-50 md:text-[46px]">
              {special.title}
            </h1>
            <p className="mt-3 max-w-2xl text-[13px] leading-relaxed text-primary-100/85 md:text-[15px]">
              {special.intro}
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-3 font-label text-[11px] text-primary-200/85">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-star-line text-accent-300" />
                {special.curator}
              </span>
              <span className="h-3 w-[1px] bg-background-50/25" />
              <span>策划于 {formatDate(special.publishedAt)}</span>
              <span className="h-3 w-[1px] bg-background-50/25" />
              <span>{specialFilms.length} 部片单</span>
            </div>
            <div className="mt-4 flex flex-wrap gap-1.5">
              {special.tags.map((tag) => (
                <span key={tag} className="rounded-full border border-background-50/25 px-2.5 py-1 text-[10px] text-primary-100/85">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-shell px-4 pt-10 md:px-8 md:pt-12">
        <SectionHeading
          kicker="The Line-up"
          title="策划片单"
          description="以下顺序即为建议观看顺序，由编辑根据叙事节奏与主题递进排列。"
        />
        <div className="grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-3 md:gap-x-5 lg:grid-cols-4">
          {specialFilms.map((film) => (
            <FilmCard key={film.id} film={film} />
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-shell px-4 pt-12 md:px-8 md:pt-14">
        <div className="grid grid-cols-1 gap-8 rounded-lg border border-background-200 bg-background-100/60 p-5 md:p-8 lg:grid-cols-[0.8fr_1.4fr] lg:gap-14">
          <div>
            <p className="flex items-center gap-2 font-label text-[11px] font-semibold uppercase tracking-[0.22em] text-accent-700">
              <span className="h-[2px] w-5 bg-accent-500" />
              Why This List
            </p>
            <h2 className="mt-2.5 font-heading text-[22px] font-extrabold leading-tight text-foreground-950 md:text-[26px]">
              推荐理由
            </h2>
            <p className="mt-3 text-[12px] leading-relaxed text-foreground-500">
              这份片单由编辑部在完整重看后确定，不包含未上线或无法查证的作品。
            </p>
          </div>
          <div className="space-y-5">
            {special.reasons.map((reason, index) => (
              <div key={index} className="flex gap-3.5">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-accent-500 font-label text-[11px] font-bold text-primary-950">
                  {index + 1}
                </span>
                <p className="text-[13px] leading-[1.9] text-foreground-700">{reason}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {others.length > 0 && (
        <section className="mx-auto max-w-shell px-4 pt-12 md:px-8 md:pt-14">
          <SectionHeading kicker="More Specials" title="相关专题" actionLabel="全部专题" actionTo="/specials" />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {others.map((item) => (
              <Link
                key={item.id}
                to={`/specials/${item.id}`}
                className="group flex cursor-pointer items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300"
              >
                <div className="relative h-[74px] w-[118px] shrink-0 overflow-hidden rounded-md bg-secondary-200">
                  <img src={item.cover} alt={item.title} className="h-full w-full object-cover object-center" />
                </div>
                <div className="min-w-0">
                  <p className="font-label text-[10px] uppercase tracking-[0.16em] text-secondary-500">{item.subtitle}</p>
                  <h3 className="mt-1 truncate font-heading text-[15px] font-bold text-foreground-950 group-hover:text-primary-700">
                    {item.title}
                  </h3>
                  <p className="mt-1 font-label text-[11px] text-foreground-500">{item.filmIds.length} 部作品</p>
                </div>
                <i className="ri-arrow-right-line ml-auto shrink-0 text-foreground-400 transition-transform group-hover:translate-x-0.5" />
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}